import { motion } from "framer-motion";
import { FileCheck2, UserCheck, Ticket, ReceiptText } from "lucide-react";

const STEPS = [
  {
    icon: FileCheck2,
    num: "01",
    title: "Request & policy validation",
    desc: "Employee submits destination, dates, and logistics preferences. The tier engine instantly validates class entitlements, hotel caps, and per diem — before a manager ever sees it.",
    tags: ["Trip wizard", "Tier L1–L3 rules", "Per diem auto-calc"],
  },
  {
    icon: UserCheck,
    num: "02",
    title: "Manager & budget approval",
    desc: "Requests route to the line manager with live project budget context. Out-of-tier upgrades trigger a hard stop requiring executive sign-off — nothing slips through.",
    tags: ["Budget burn meter", "Hard-stop exceptions", "Mobile approvals"],
  },
  {
    icon: Ticket,
    num: "03",
    title: "Travel desk ticketing",
    desc: "Approved requests land on the travel desk queue. Agents book via negotiated rates and upload the final itinerary — PNRs, hotel references, cab vouchers — straight into the trip record.",
    tags: ["GDS booking", "Itinerary vault", "Refund credit shells"],
  },
  {
    icon: ReceiptText,
    num: "04",
    title: "Expense settlement",
    desc: "Post-trip, employees snap receipts; OCR itemizes and cross-checks against per diem. Finance verifies in bulk, deducts cash advances, and pushes settlement to the ERP.",
    tags: ["Receipt OCR", "Advance deduction", "ERP payout export"],
  },
];

export const Workflow = () => {
  return (
    <section id="workflow" data-testid="workflow-section" className="bg-[#090d16] noise-bg py-24 lg:py-32 scroll-mt-16">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-2xl">
          <p className="font-mono text-[11px] tracking-widest text-blue-400 uppercase">End-to-end lifecycle</p>
          <h2 className="mt-4 font-heading font-extrabold tracking-tight text-white text-4xl sm:text-5xl">
            From request to reimbursement in four moves
          </h2>
        </div>

        <div className="mt-16 grid md:grid-cols-2 xl:grid-cols-4 gap-6">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.num}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.12, duration: 0.55 }}
              data-testid={`workflow-step-${i + 1}`}
              className="group relative rounded-xl border border-white/10 bg-slate-900/60 p-7 hover:border-blue-500/50 hover:-translate-y-1 transition-all duration-300"
            >
              <div className="flex items-center justify-between">
                <span className="w-11 h-11 rounded-lg bg-blue-600/15 border border-blue-500/30 flex items-center justify-center text-blue-400">
                  <s.icon className="w-5 h-5" />
                </span>
                <span className="font-mono text-sm text-slate-600">{s.num}</span>
              </div>
              <h3 className="mt-6 font-heading font-bold text-lg text-white">{s.title}</h3>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {s.tags.map((t) => (
                  <span key={t} className="px-2.5 py-1 rounded-full border border-white/10 font-mono text-[10px] tracking-wide text-slate-400">
                    {t}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
